/**
 * 📋 SDLC 6.5.0 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/components/common/PortalMenu.tsx
 * PURPOSE: Dropdown menu render qua React portal + position:fixed theo rect của nút mở,
 *          tránh bị container overflow:hidden/auto cắt đứng ở mép card (F-6/F-7/F-8
 *          trong FINDINGS-KIMI-PREFLIGHT-RELV0-2026-08-04). Tự lật mở lên trên khi
 *          sát đáy viewport.
 * SDLC FRAMEWORK VERSION: 6.5.0 (7-Pillar + 9 Mental Models + LRAP)
 * DATE: 2026-09-14
 * OWNER: @coder_frontend
 *
 * @technical-architecture
 * - Pattern: createPortal vào document.body + fixed positioning; đo offsetHeight sau
 *   render đầu để quyết hướng mở (xuống mặc định, lên khi không đủ chỗ)
 * - Close: click ngoài (mousedown) · Escape · scroll/resize window
 * - mousedown trong menu stopPropagation để không bị document-level close handler của
 *   trang gỡ menu trước khi onClick của item kịp chạy
 * - `ariaLabel` (2026-09-14, ManagerSchedulePage SC-009 toolbar-dropdown port): prop
 *   optional để set `aria-label` trên chính div `role="menu"` — cần vì component tự
 *   set `role="menu"` cứng, nên KHÔNG thể bọc thêm `role="menu"` khác ở children
 *   (trùng role). Mặc định undefined = không đổi hành vi 5 consumer cũ bên dưới.
 *
 * @cross-references
 * - Findings: docs/08-collaborate/09-handoffs/FINDINGS-KIMI-PREFLIGHT-RELV0-2026-08-04.md (F-6/F-7/F-8)
 * - Consumers: TenantUserManagement.tsx · OrganizationManagement/index.tsx · UnitTypesTab.tsx ·
 *   BranchGeofenceTab.tsx · ManagerStaffPage.tsx · ManagerAttendancePage.tsx ·
 *   ManagerSchedulePage.tsx (4 toolbar dropdown — Nhân viên/Khu vực/Xem/Xuất, 2026-09-14)
 * ===============================================================================
 */
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

export interface PortalMenuAnchor {
  /** rect.top của nút mở (viewport coords) */
  top: number;
  /** rect.bottom của nút mở (viewport coords) */
  bottom: number;
  /** khoảng cách từ mép PHẢI viewport tới mép phải nút mở: window.innerWidth - rect.right */
  right: number;
  /**
   * Mở SANG PHẢI từ toạ độ này thay vì căn phải theo `right` — dùng khi menu
   * phải nằm NGOÀI khối đang mở nó (vd popup "Lịch sử xử lý" cạnh modal chi
   * tiết: căn phải sẽ đè lên chính nội dung modal). Bỏ trống = căn phải như cũ.
   */
  left?: number;
}

interface PortalMenuProps {
  anchor: PortalMenuAnchor;
  onClose: () => void;
  children: React.ReactNode;
  minWidth?: number;
  /** style bổ sung cho khung menu (vd padding) */
  style?: React.CSSProperties;
  /** aria-label cho div `role="menu"` (component tự set role, không bọc thêm được ở children) */
  ariaLabel?: string;
}

export const PortalMenu: React.FC<PortalMenuProps> = ({
  anchor,
  onClose,
  children,
  minWidth = 160,
  style,
  ariaLabel,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [flipUp, setFlipUp] = useState(false);

  // Đo chiều cao thật sau render đầu → lật lên nếu mở xuống sẽ tràn đáy viewport
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const h = el.offsetHeight;
    const overflowDown = anchor.bottom + 2 + h > window.innerHeight;
    const roomUp = anchor.top - 2 - h > 0;
    setFlipUp(overflowDown && roomUp);
  }, [anchor]);

  useEffect(() => {
    const onDocMouseDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    const onScrollResize = () => onClose();
    document.addEventListener("mousedown", onDocMouseDown);
    document.addEventListener("keydown", onKey);
    window.addEventListener("scroll", onScrollResize, true);
    window.addEventListener("resize", onScrollResize);
    return () => {
      document.removeEventListener("mousedown", onDocMouseDown);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", onScrollResize, true);
      window.removeEventListener("resize", onScrollResize);
    };
  }, [onClose]);

  const side: React.CSSProperties =
    anchor.left != null ? { left: anchor.left } : { right: anchor.right };
  const pos: React.CSSProperties = flipUp
    ? { bottom: window.innerHeight - anchor.top + 2, ...side }
    : { top: anchor.bottom + 2, ...side };

  return createPortal(
    <div
      ref={ref}
      role="menu"
      aria-label={ariaLabel}
      onMouseDown={(e) => e.stopPropagation()}
      style={{
        position: "fixed",
        ...pos,
        zIndex: 2000,
        background: "white",
        border: "1px solid #e5e7eb",
        borderRadius: "8px",
        boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
        minWidth,
        overflow: "hidden",
        ...style,
      }}
    >
      {children}
    </div>,
    document.body
  );
};

export default PortalMenu;
